/** Pure logic of the "ABC XY" dialog: form values <-> product line props, validation. */
import type { ProductLine } from './api';

export interface XyValues {
  customScale: number;
  customX: number;
  customY: number;
  textScale: number;
  textX: number;
  textY: number;
  textRotate: number;
  textCurve: number;
  textLetterSpace: number;
}

export const DEFAULTS: XyValues = {
  customScale: 100,
  customX: 0,
  customY: 0,
  textScale: 100,
  textX: 0,
  textY: 0,
  textRotate: 0,
  textCurve: 0,
  textLetterSpace: 0,
};

/**
 * The record's `imageProps.custom` and `textProps`.
 * A missing prop leaves its field empty (NaN) and is not sent back on save.
 */
export const productLineToXy = (data: ProductLine): XyValues => ({
  customScale: data.imageProps?.custom?.scale ?? Number.NaN,
  customX: data.imageProps?.custom?.offsetX ?? Number.NaN,
  customY: data.imageProps?.custom?.offsetY ?? Number.NaN,
  textScale: data.textProps?.scale ?? Number.NaN,
  textX: data.textProps?.offsetX ?? Number.NaN,
  textY: data.textProps?.offsetY ?? Number.NaN,
  textRotate: data.textProps?.rotate ?? Number.NaN,
  textCurve: data.textProps?.curve ?? Number.NaN,
  textLetterSpace: data.textProps?.letterSpace ?? Number.NaN,
});

const orUndefined = (value: number): number | undefined =>
  Number.isNaN(value) ? undefined : value;
export const xyToProductLine = (values: XyValues) => ({
  textProps: {
    scale: orUndefined(values.textScale),
    offsetX: orUndefined(values.textX),
    offsetY: orUndefined(values.textY),
    rotate: orUndefined(values.textRotate),
    curve: orUndefined(values.textCurve),
    letterSpace: orUndefined(values.textLetterSpace),
  },
  imageProps: {
    custom: {
      scale: orUndefined(values.customScale),
      offsetX: orUndefined(values.customX),
      offsetY: orUndefined(values.customY),
    },
  },
});

const inRange = (value: number, min: number, max: number) =>
  Number.isNaN(value) || (value >= min && value <= max);

export const validateXy = (
  values: XyValues,
): Partial<Record<keyof XyValues, string>> => {
  const errors: Partial<Record<keyof XyValues, string>> = {};
  if (!inRange(values.customScale, 0, 1000)) errors.customScale = 'Wrong scale';
  if (!inRange(values.textScale, 0, 1000)) errors.textScale = 'Wrong scale';
  return errors;
};
